/*  ==|== Responsive =============================================================
 Author: James Jackson-South
 twitter : http://twitter.com/James_M_South
 github : https://github.com/ResponsiveBP/Responsive
 Copyright (c),  James Jackson-South.
 Licensed under the MIT License.
 ============================================================================== */

/*! Responsive v4.1.4 | MIT License | responsivebp.com */

import $ from "jquery";
import { createResponsiveEventsName, getDataOptions, keys, onTransitionEnd, redraw, supportRtl, supportTransition } from "./responsive.core";

declare global {
  interface Window {
    RESPONSIVE_DROPDOWN?: boolean;
  }
}

interface DropdownOptions {
  target: string;
  parent?: string;
  dimension?: "height" | "width";
}

const ns = ".r.dropdown";
const da = ".data-api";

const { einit, eshown, eshow, echanged } = createResponsiveEventsName(ns, da);
const ehide = "hide" + ns;
const ehidden = "hidden" + ns;

class Dropdown {
  $element: JQuery;
  $target: JQuery;
  $parent: JQuery | null;
  transitioning = false;
  options: DropdownOptions;

  constructor(element: HTMLElement, options: DropdownOptions) {
    this.$element = $(element);
    this.options = $.extend({ dimension: "height" }, options);
    this.$target = $(this.options.target);
    this.$parent = this.options.parent
      ? this.$target.closest(this.options.parent)
      : null;
  }

  init() {
    this.initAccessibility();
    this.bindEvents();
  }

  private initAccessibility() {
    const id = this.$element.attr("id") || `dropdown-${Date.now()}`;
    const paneId = this.$target.attr("id") || `dropdown-${Date.now()}`;
    const active = !this.$target.hasClass("collapse");

    this.$element.attr({
      id,
      role: "tab",
      "aria-controls": paneId,
      "aria-selected": active,
      "aria-expanded": active,
      tabindex: 0,
    });

    if (this.$parent) {
      this.$parent.attr({
        role: "tablist",
        "aria-multiselectable": "true",
      });

      // We're safe to add the attribute here since if it's not used then
      // data-api is disabled.
      this.$element.attr({
        "data-dropdown-parent": this.options.parent,
      });
    }

    this.$target.attr({
      id: paneId,
      role: "tabpanel",
      "aria-labelledby": id,
      "aria-hidden": !active,
      hidden: !active,
    });
  }

  private bindEvents() {
    this.$element.on("click", this.click.bind(this));
    this.$element.on("keydown", this.keydown.bind(this));
  }

  private show() {
    if (this.transitioning || this.$target.hasClass("expand")) return;
    this.$target.removeAttr('inert');

    this.transitioning = true;

    let $actives: JQuery[] = [];

    const dimension = this.options.dimension;
    let size = "";

    if (this.$parent) {
      const all = this.$parent.find(
        `[data-dropdown-parent="${this.options.parent}"]`,
      );

      $actives = $.grep(all, (el) =>
      {
        const data = $(el).data("r.dropdown") as Dropdown;
        return (
          data &&
          data.$target &&
          !data.$target.hasClass("collapse") &&
          data.$parent &&
          data.$parent[0] === this.$parent![0]
        );
      }) as JQuery[];
    }

    // Set the height/width to zero then to the height/width
    // so animation can take place.
    this.$target[dimension](0);

    if (supportTransition()) {

      // Calculate the height/width.
      this.$target[dimension]("auto").attr({ "aria-hidden": false, "hidden": false });
      this.$target.find("[tabindex]:not(.collapse)").attr({ "aria-hidden": false, "hidden": false });
      size = window.getComputedStyle(this.$target[0])[dimension];

      // Reset to zero and force repaint.
      redraw(this.$target[dimension](0));
    }

    this.$target[dimension](size);

    this.transition("removeClass", $.Event(eshow), eshown);

    if ($actives.length) {
      $actives.forEach((el) =>
      {
        const $el = $(el);
        applyDropdown($el, "hide");
        $el.dropdown("hide");
      });
    }
  }

  private hide() {
    if (this.transitioning || this.$target.hasClass("collapse")) return;
    this.transitioning = true;

    // Reset the height/width and then reduce to zero.
    let dimension = this.options.dimension;
    let size = "";


    if (supportTransition()) {

      // Set the height to auto, calculate the height/width and reset.
      size = window.getComputedStyle(this.$target[0])[dimension];

      // Reset the size and force repaint.
      redraw(this.$target[dimension](size)); // Force reflow ;
    }

    this.$target.removeClass("expand");
    this.$target[dimension](0);
    this.transition("addClass", $.Event(ehide), ehidden);
    this.$target.attr('inert', 'true');
  }

  private toggle() {
    if (this.transitioning) return;

    // Run the correct command based on the presence of the class "collapse".
    this[this.$target.hasClass("collapse") ? "show" : "hide"]();
  }

  private transition(
    method: "addClass" | "removeClass",
    startEvent: JQuery.Event,
    completeEvent: string,
  ) {
    this.$element.trigger(startEvent);
    if (startEvent.isDefaultPrevented()) return;

    const doShow = method === "removeClass";

    const complete = () =>
      {
        // Ensure the height/width is set to auto.
        this.$target
          .removeClass("trans")[this.options.dimension!]("");

        // Set the correct aria attributes.
        this.$target.attr({
          "aria-hidden": !doShow,
          hidden: !doShow,
        });

        $("#" + this.$target.attr("aria-labelledby")).attr({
          "aria-selected": doShow,
          "aria-expanded": doShow,
        });

        if (doShow) {
          this.$element.focus();
        }

        // Toggle any children.
        this.$target.find("[tabindex]:not(.collapse)").attr({
          "aria-hidden": !doShow,
          hidden: !doShow,
          tabindex: doShow ? 0 : -1,
        });

        this.transitioning = false;
        this.$element.trigger($.Event(completeEvent, { relatedTarget: this.options.target }));
      };

    // Remove or add the expand classes.
    this.$target[method]("collapse");
    this.$target[startEvent.type === "show" ? "addClass" : "removeClass"]("trans expand");

    onTransitionEnd(this.$target.get(0), complete);
  }

  private click(event: JQuery.ClickEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.toggle();
  }

  private keydown(event: JQuery.KeyDownEvent) {
    if (/input|textarea/i.test((
      event.target as HTMLElement
    ).tagName)) return;

    const which = event.which!;

    if ([keys.SPACE, keys.LEFT, keys.RIGHT].includes(which)) {
      event.preventDefault();
      event.stopPropagation();

      const $this = $(event.target);
      const $parent = this.options.parent
        ? $this.closest("[role=tablist]")
        : $this.closest(".accordion");

      const $items = $parent.find(`[data-dropdown-parent="${this.options.parent}"]`);
      let index = $items.index($items.filter(":focus"));
      const length = $items.length;

      if (which === keys.LEFT) {
        index = (
          supportRtl() ? index + 1 : index - 1 + length
        ) % length;
      } else if (which === keys.RIGHT) {
        index = (
          supportRtl() ? index - 1 + length : index + 1
        ) % length;
      } else if (which === keys.SPACE) {
        this.toggle();
        return;
      }

      const data = $($items.eq(index)).data("r.dropdown") as Dropdown;
      data?.show();
    }
  }
}

const applyDropdown = (
  $elements: JQuery,
  option?: DropdownOptions | "show" | "hide" | "toggle",
): void =>
  {
    $elements.each(function()
    {
      const $this = $(this);
      let dropdown = $this.data("r.dropdown") as Dropdown;

      if (!dropdown && typeof option !== "string") {
        const opts = $.extend({}, option) as DropdownOptions;
        dropdown = new Dropdown(this, opts);
        dropdown.init();
        $this.data("r.dropdown", dropdown);
      }

      if (typeof option === "string" && /^(show|hide|toggle)$/.test(option)) {
        dropdown?.[option]();
      }
    });
  };

const init = () =>
  {
    if (window.RESPONSIVE_DROPDOWN) return;
    window.RESPONSIVE_DROPDOWN = true;

    const prepare = () =>
      {
        $("[data-dropdown-target]").each(function()
        {
          const $this = $(this);
          if (!$this.data("r.dropdownLoaded")) {
            $this.data("r.dropdownLoaded", true);
            const options = getDataOptions($this, "dropdown") as DropdownOptions;
            applyDropdown($this, options);
          }
        });
      };

    $(document)
      .on([einit, echanged].join(" "), prepare)
      .ready(() => $(document).trigger(einit));
  };

export default { init };
